import * as Yup from 'yup'

import { isAdInventoryTargetingEnabled } from './formatters/formatInventoryTargeting'
import { getAllocationInfo } from '../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/helpers'
import { getConditionalNewListItemValidation } from '../../../../../hooks/formHooks/listItemFormHooks/helpers'
import { formatNumber } from '../../../../../helpers/numbers'
import { getInventoryItemId } from './helpers'

import { LINE_ITEM } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeLineItemForm/fields'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'
import {
  END_DATE,
  endDateValidation,
  START_DATE,
  startDateValidation
} from '../../../../ReusableFormFields/StartEndDateRangeFields/fields'
import {
  MAX_IMPRESSIONS,
  TIME_UNIT,
  TIME_UNIT_COUNT,
  timeUnits
} from './Steps/DatesStep/AdvancedSettings/FrequencyCapSection/fields'
import {
  RIGHT_INVENTORY_SELECT,
  TARGETING_OPTION,
  targetingOptions,
  UNITS,
  ATTACH_TARGETING_OPTIONS,
  GEO_TARGETING,
  DEVICE_CATEGORY,
  BROWSER_LANGUAGE_TARGETING,
  DEVICE_MANUFACTURER_TARGETING,
  OPERATING_SYSTEM_TARGETING,
  AUDIENCE_SEGMENTS,
  CUSTOM_TARGETING,
  CONTENT_TARGETING
} from './fields'
import {
  AD_INVENTORY,
  CUSTOM_TARGETING_ENABLED,
  DEVICE_TARGETING_ENABLED,
  GEO_TARGETING_ENABLED,
  IS_SUB_ALLOCATION
} from '../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/fields'
import { OPTION_YES } from '../../../../../constants/forms'

import { getGamInventoryType, inventoryTypes } from './helpers'

const getTargetingOptionsValidation = right => {
  const rightAdvancedSettings = right?.advanced_settings || {}
  const {
    [GEO_TARGETING_ENABLED]: geoTargetingEnabled,
    [DEVICE_TARGETING_ENABLED]: deviceTargetingEnabled,
    [CUSTOM_TARGETING_ENABLED]: customTargetingEnabled
  } = rightAdvancedSettings

  // Create validation that requires at least one targeting option when "Yes" is selected
  const hasAnyTargetingOption = values => {
    const targetingFields = []

    if (geoTargetingEnabled) {
      targetingFields.push(values[GEO_TARGETING])
    }
    if (deviceTargetingEnabled) {
      targetingFields.push(
        values[DEVICE_CATEGORY],
        values[BROWSER_LANGUAGE_TARGETING],
        values[DEVICE_MANUFACTURER_TARGETING],
        values[OPERATING_SYSTEM_TARGETING]
      )
    }
    if (customTargetingEnabled) {
      targetingFields.push(values[AUDIENCE_SEGMENTS], values[CUSTOM_TARGETING], values[CONTENT_TARGETING])
    }

    // Check if any targeting field has values
    return targetingFields.some(field => field && field.length > 0)
  }

  return {
    [ATTACH_TARGETING_OPTIONS]: Yup.string().test(
      'targeting-options-required',
      'Please select at least one option',
      function (value) {
        if (value === OPTION_YES) {
          return hasAnyTargetingOption(this.parent)
        }
        return true
      }
    )
  }
}

const getPercentageAllocationInventoryValidation = right => {
  const adInventoryTargetingEnabled = isAdInventoryTargetingEnabled(right)
  const inventoryType = getGamInventoryType(right[AD_INVENTORY])

  return adInventoryTargetingEnabled
    ? {
        // Percentage allocation: only validate inventory item selection for specific targeting
        [RIGHT_INVENTORY_SELECT]: Yup.array().when([TARGETING_OPTION], {
          is: targetingOption => targetingOption === targetingOptions.SPECIFIC_INVENTORY && adInventoryTargetingEnabled,
          then: () => {
            if (inventoryType === inventoryTypes.placements) {
              return Yup.array().min(1, 'At least one placement is required')
            } else {
              return Yup.array().min(1, 'At least one ad unit is required')
            }
          }
        })
      }
    : {}
}

const getRemainingBudgetValidation = remainingBudget => {
  return Yup.number()
    .required('Required')
    .positive('Budget must be positive')
    .test('remaining-budget', 'Exceeds remaining budget', function (value) {
      if (value > remainingBudget) {
        return this.createError({
          message: `Budget cannot exceed remaining budget of ${formatNumber(remainingBudget, { max: 2 })}`
        })
      }

      return true
    })
}

const getSubAllocationInventoryItemValidation = inventoryBudgets => {
  return Yup.number()
    .required('Required')
    .positive('Budget must be positive')
    .test('inventory-remaining-budget', 'Exceeds remaining budget', function (value) {
      const inventoryItem = this.parent
      const inventoryId = getInventoryItemId(inventoryItem)

      if (inventoryId && inventoryBudgets[inventoryId]) {
        const inventoryBudget = inventoryBudgets[inventoryId]
        if (value > inventoryBudget.remaining) {
          return this.createError({
            message: `Budget cannot exceed remaining budget of ${formatNumber(inventoryBudget.remaining, { max: 2 })} for this inventory item`
          })
        }
      }

      return true
    })
}

const getQuantityAllocationInventoryValidation = (right, remainingBudget, inventoryBudgets = {}) => {
  const adInventoryTargetingEnabled = isAdInventoryTargetingEnabled(right)
  const isSubAllocation = right[IS_SUB_ALLOCATION]

  if (isSubAllocation) {
    return {
      [RIGHT_INVENTORY_SELECT]: Yup.array()
        .of(
          Yup.object().shape({
            [UNITS]: getSubAllocationInventoryItemValidation(inventoryBudgets)
          })
        )
        .min(1, 'At least one placement is required')
    }
  }

  if (adInventoryTargetingEnabled) {
    return {
      [UNITS]: getRemainingBudgetValidation(remainingBudget),
      [RIGHT_INVENTORY_SELECT]: Yup.array().when([TARGETING_OPTION], {
        is: targetingOption => targetingOption === targetingOptions.SPECIFIC_INVENTORY,
        then: () => Yup.array().min(1, 'At least one placement is required')
      })
    }
  }

  return {
    [UNITS]: getRemainingBudgetValidation(remainingBudget)
  }
}

const getInventoryValidation = (right, remainingBudget, inventoryBudgets) => {
  const { isPercentageAllocation } = getAllocationInfo(right)

  if (isPercentageAllocation) {
    return getPercentageAllocationInventoryValidation(right)
  } else {
    return getQuantityAllocationInventoryValidation(right, remainingBudget, inventoryBudgets)
  }
}

const getNewLineItemValidation = (right, remainingBudget, inventoryBudgets) => {
  return {
    [START_DATE]: startDateValidation,
    [END_DATE]: endDateValidation,

    [TIME_UNIT_COUNT]: Yup.string().when([MAX_IMPRESSIONS, TIME_UNIT], {
      is: (maxImpressions, timeUnit) =>
        maxImpressions && maxImpressions.trim() !== '' && timeUnit !== timeUnits.LIFETIME,
      then: () => Yup.string().required('Please enter time unit count')
    }),

    ...getInventoryValidation(right, remainingBudget, inventoryBudgets),
    ...getTargetingOptionsValidation(right)
  }
}

export const getValidationSchema = (right, remainingBudget = 0, inventoryBudgets = {}) => {
  const newLineItemValidationObject = getNewLineItemValidation(right, remainingBudget, inventoryBudgets)

  const newLineItemValidation = getConditionalNewListItemValidation(newLineItemValidationObject)

  return Yup.object({
    // line item
    [LINE_ITEM]: Yup.string().when(LIST_ITEM_OPTION, {
      is: OPTION_EXISTING_LIST_ITEM,
      then: () => Yup.string().required('Please select line item')
    }),

    ...newLineItemValidation
  })
}
